import { useState } from "react";
import { useForm } from "react-hook-form";
import { useSession } from "@/platform/auth";
import { useAuth } from "@/platform/auth";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/shared/components/ui/alert";
import { Mail, CircleAlert, CircleCheck } from "lucide-react";
import { Navigate } from "react-router";

export function ResetPasswordPage() {
  const { user } = useSession();
  const { requestPasswordReset } = useAuth();
  type ResetFormValues = { email: string };
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setError,
    clearErrors,
  } = useForm<ResetFormValues>({
    defaultValues: { email: "" },
  });

  const [requestSent, setRequestSent] = useState(false);

  const onSubmit = async ({ email }: ResetFormValues) => {
    clearErrors("root");
    setRequestSent(false);
    const { error } = await requestPasswordReset(email);
    if (error) setError("root", { type: "server", message: error });
    else setRequestSent(true);
  };

  if (user) return <Navigate to="/" replace />;

  return (
    <>
      <title>Reset Password - Researchly</title>
      <div className="flex min-h-[70vh] items-center justify-center p-4">
        <div className="w-full max-w-md rounded-xl border bg-white p-6">
          <h1 className="mb-1 text-2xl font-semibold">Reset your password</h1>
          <p className="mb-6 text-sm text-muted-foreground">
            Enter your email and we'll send you a reset link.
          </p>
          <form
            className="space-y-3"
            onSubmit={handleSubmit(onSubmit)}
            noValidate
          >
            <label className="text-sm font-medium" htmlFor="email">
              Email
            </label>
            <Input
              id="email"
              type="email"
              placeholder="you@example.com"
              autoComplete="email"
              aria-invalid={!!errors.email}
              aria-describedby={errors.email ? "email-error" : undefined}
              {...register("email", {
                required: "Email is required",
                pattern: {
                  value: /^\S+@\S+$/i,
                  message: "Enter a valid email",
                },
              })}
            />
            {errors.email && (
              <p
                id="email-error"
                className="text-xs text-destructive"
                aria-live="polite"
              >
                {errors.email.message}
              </p>
            )}
            <Button type="submit" className="w-full" disabled={isSubmitting}>
              <Mail className="mr-2 h-4 w-4" /> Send reset link
            </Button>
          </form>
          {errors.root && (
            <Alert variant="destructive" className="mt-3">
              <CircleAlert />
              <AlertTitle>Request failed</AlertTitle>
              <AlertDescription>{errors.root.message}</AlertDescription>
            </Alert>
          )}
          {requestSent && (
            <Alert className="mt-3">
              <CircleCheck />
              <AlertTitle>Check your email</AlertTitle>
              <AlertDescription>
                If an account exists for that email, a reset link has been sent.
              </AlertDescription>
            </Alert>
          )}
        </div>
      </div>
    </>
  );
}
